import React, { Suspense, useContext } from 'react';
import { matchPath, useLocation } from 'react-router';
import useLocalStorage from 'react-use/lib/useLocalStorage';
import { Box, Stack, Toolbar } from '@mui/material';

import { useRouter } from '@cvt/routing';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';

import { Authenticated } from '@modules/Auth/components';

import { LayoutContext } from '@shared/contexts/LayoutContext';
import { continueToDesktopStorageKey } from '@shared/config';

import { Navigation } from '../Navigation';
import { Header } from '../Header';
import { DesktopMessage } from '../DesktopMessage';


export const MainLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const location = useLocation();
  const [allowDesktop, setAllowDesktop] = useLocalStorage(continueToDesktopStorageKey);

  const { isDesktop } = useContext(LayoutContext);

  const [navigationOpen, setNavigationOpen] = React.useState(false);

  const openNavigation = () => {
    setNavigationOpen(true);
  };

  const closeNavigation = () => {
    setNavigationOpen(false);
  };

  const toggleNavigation = () => {
    setNavigationOpen(open => !open);
  };

  const hideHeader = React.useMemo(() => {
    const paths: string[] = [
      router.auth.login.path,
      router.auth.signup(':companySlug?').path,
      router.auth.resetPassword().path,
      router.auth.resetPasswordConfirm.path,
      router.onboarding.path,
    ];
    return paths.some(path => matchPath(path, location.pathname));
  }, [location, router]);

  const hideNavigation = React.useMemo(() => {
    const paths: string[] = [
      router.onboarding.path,
    ];
    return paths.some(path => matchPath(path, location.pathname));
  }, [location, router]);

  const showDesktopMessage = isDesktop && !allowDesktop;

  const isAuthPage = hideHeader;

  const onContinueToDesktop = () => {
    setAllowDesktop(true);
  };

  return (
    <Stack width="100%" height="100%">
      {!hideHeader && <Header onNavigationClickRequest={toggleNavigation} />}
      {showDesktopMessage && !isAuthPage ? (
        <DesktopMessage onContinueToDesktop={onContinueToDesktop} />
      ) : (
        <>
          <Stack flex="auto" direction={{ xs: 'column', md: 'row' }}>
            {!hideNavigation && isDesktop && (
              <Authenticated>
                <Navigation open={navigationOpen} onOpenRequest={openNavigation} onCloseRequest={closeNavigation} />
              </Authenticated>
            )}
            <Stack width="100%" height="100%" direction="column">
              <Box component="main" width="100%" height="calc(100% - env(safe-area-inset-top) - env(safe-area-inset-bottom))">
                <Suspense key={location.pathname} fallback={<BodyLoading height="100%"/>}>
                  <Authenticated>
                    {!hideHeader && <Toolbar />}
                  </Authenticated>
                  {children}
                  <Authenticated>
                    {!isDesktop && (
                      <>
                        <Toolbar />
                        <Box height="env(safe-area-inset-bottom)" />
                      </>
                    )}
                  </Authenticated>
                </Suspense>
              </Box>
            </Stack>
          </Stack>
          {!hideNavigation && !isDesktop && (
            <Authenticated>
              <Box position="fixed" bottom={0} left={0} right={0} zIndex={100} pb="env(safe-area-inset-bottom)" bgcolor="common.white" borderTop="2px solid #000">
                <Navigation open={navigationOpen} onOpenRequest={openNavigation} onCloseRequest={closeNavigation} />
              </Box>
            </Authenticated>
          )}
        </>
      )}
    </Stack>
  );
};
